import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import { Truck, MapPin, Calendar, ClipboardCopy, Clock, Package, User, Search, AlertCircle } from "lucide-react"
import { formatCurrency } from "@/utils/format"
import axios from "@/utils/axios"
import { toast } from "sonner"
import Seo from "@/seo/seo"
import { routeSeoConfig } from "@/utils/seo-config"

export default function TrackOrderPage() {
  const [email, setEmail] = useState("")
  const [orderId, setOrderId] = useState("")
  const [orderData, setOrderData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const handleTrackOrder = async (e) => {
    e?.preventDefault()
    
    if (!email || !orderId) {
      setError("Please enter both email and order ID")
      return
    }

    setLoading(true)
    setError("")
    setOrderData(null)

    try {
      const response = await axios.post("/orders/track", {
        orderId: orderId.trim(),
        email: email.trim(),
      })

      if (response.data?.success && response.data?.data) {
        setOrderData(response.data.data)
        setError("")
      } else {
        setError("Invalid response from server. Please try again.")
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to track order. Please check your email and order ID."
      setError(errorMessage)
      setOrderData(null)
      if (err.response?.status !== 404 && err.response?.status !== 401) {
        toast.error(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadgeColor = (status) => {
    if (!status) return "bg-gray-100 text-gray-700"
    switch (status.toLowerCase()) {
      case "delivered":
        return "bg-green-100 text-green-700"
      case "shipped":
        return "bg-blue-100 text-blue-700"
      case "processing":
        return "bg-yellow-100 text-yellow-700"
      case "pending":
        return "bg-gray-100 text-gray-700"
      case "cancelled":
        return "bg-red-100 text-red-700"
      default:
        return "bg-gray-100 text-gray-700"
    }
  }

  const formatDateTime = (isoString) => {
    if (!isoString) return "N/A"
    const date = new Date(isoString)
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })
  }

  const getStatusIcon = (status) => {
    if (!status) return Clock
    switch (status.toLowerCase()) {
      case "delivered":
        return Package
      case "shipped":
        return Truck
      case "processing":
        return ClipboardCopy
      default:
        return Clock
    }
  }

  // Format address value (can be object with label or string)
  const formatAddressValue = (value) => {
    if (!value) return "N/A"
    if (typeof value === "object" && value?.label) return value.label
    if (typeof value === "object" && value?.value) return value.value
    return value
  }

  // Parse shipping address
  const getShippingAddress = () => {
    if (!orderData?.shippingAddress) return null
    
    if (typeof orderData.shippingAddress === "string") {
      try {
        return JSON.parse(orderData.shippingAddress)
      } catch {
        return { raw: orderData.shippingAddress }
      }
    }
    return orderData.shippingAddress
  }

  return (
    <>
      <Seo {...routeSeoConfig.trackOrder} type="website" noindex={routeSeoConfig.trackOrder.noindex} />
      <div className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          {/* Page Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Truck className="w-10 h-10 text-primary" />
              <h1 className="text-4xl font-bold text-primary">Track Your Order</h1>
            </div>
            <p className="text-gray-600 text-lg">Enter your email and order ID to track your shipment</p>
          </div>

          {/* Search Form */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-primary">Order Tracking</CardTitle>
              <CardDescription>Please provide your order details to track your shipment</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleTrackOrder}>
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1"
                      required
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <Label htmlFor="orderId">Order ID</Label>
                    <Input
                      id="orderId"
                      placeholder="Enter your order ID"
                      value={orderId}
                      onChange={(e) => setOrderId(e.target.value)}
                      className="mt-1"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-600 mb-4">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading || !email.trim() || !orderId.trim()}
                  className="w-full bg-primary hover:bg-primary/90 text-white"
                >
                  {loading ? (
                    <>
                      <Clock className="w-4 h-4 mr-2 animate-spin" />
                      Tracking Order...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Track Order
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Order Details */}
          {orderData && (
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Order Summary */}
              <div className="lg:col-span-1 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-primary">Order Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-secondary" />
                      <div>
                        <p className="font-medium text-primary">Order ID:</p>
                        <p className="text-sm text-gray-600 break-all">{orderData.orderId}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Badge className={getStatusBadgeColor(orderData.status)}>
                        {orderData.status?.charAt(0).toUpperCase() + orderData.status?.slice(1) || "Unknown"}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-secondary" />
                      <div>
                        <p className="font-medium text-primary">Order Date:</p>
                        <p className="text-sm text-gray-600">{formatDateTime(orderData.createdAt)}</p>
                      </div>
                    </div>

                    {orderData.deliveredAt && (
                      <div className="flex items-center gap-3">
                        <Package className="w-5 h-5 text-secondary" />
                        <div>
                          <p className="font-medium text-primary">Delivered:</p>
                          <p className="text-sm text-gray-600">{formatDateTime(orderData.deliveredAt)}</p>
                        </div>
                      </div>
                    )}

                    <Separator />

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Subtotal:</span>
                        <span>{formatCurrency(orderData.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Shipping:</span>
                        <span>{formatCurrency(orderData.shippingCost)}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between font-bold text-primary">
                        <span>Total:</span>
                        <span>{formatCurrency(orderData.total)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Customer & Shipping Info */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-primary">Shipping Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {orderData.user && (
                      <div className="flex items-center gap-3">
                        <User className="w-5 h-5 text-secondary" />
                        <div>
                          <p className="font-medium text-primary">{orderData.user.name || "Customer"}</p>
                          {orderData.user.email && (
                            <p className="text-sm text-gray-600">{orderData.user.email}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {getShippingAddress() && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-secondary mt-1" />
                        <div>
                          <p className="font-medium text-primary">Delivery Address:</p>
                          {getShippingAddress().address && (
                            <p className="text-sm text-gray-600">{formatAddressValue(getShippingAddress().address)}</p>
                          )}
                          {(getShippingAddress().city || getShippingAddress().state) && (
                            <p className="text-sm text-gray-600">
                              {formatAddressValue(getShippingAddress().city)}
                              {getShippingAddress().city && getShippingAddress().state && ", "}
                              {formatAddressValue(getShippingAddress().state)}
                            </p>
                          )}
                          {(getShippingAddress().postcode || getShippingAddress().country) && (
                            <p className="text-sm text-gray-600">
                              {formatAddressValue(getShippingAddress().postcode)}
                              {getShippingAddress().postcode && getShippingAddress().country && ", "}
                              {formatAddressValue(getShippingAddress().country)}
                            </p>
                          )}
                          {getShippingAddress().raw && !getShippingAddress().address && (
                            <p className="text-sm text-gray-600">{getShippingAddress().raw}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Shipments */}
                    {orderData.shipments && orderData.shipments.length > 0 && (
                      <div className="space-y-2 pt-2 border-t">
                        <p className="font-medium text-primary text-sm">Tracking Numbers:</p>
                        {orderData.shipments.map((shipment, idx) => (
                          <div key={idx} className="text-sm text-gray-600">
                            <p className="font-medium">{shipment.tracking_number}</p>
                            {shipment.courier && <p className="text-xs">via {shipment.courier}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Order History Timeline */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-primary">Order History</CardTitle>
                    <CardDescription>Track the progress of your order from placement to delivery</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {orderData.history && orderData.history.length > 0 ? (
                      <ol className="relative border-l border-gray-200 ml-4">
                        {orderData.history.map((event, index) => {
                          const StatusIcon = getStatusIcon(event.status)
                          const isLatest = index === orderData.history.length - 1
                          return (
                            <li key={index} className="mb-8 ml-6">
                              <span className="absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full bg-secondary ring-8 ring-white">
                                <StatusIcon className="h-4 w-4 text-primary" />
                              </span>
                              <h3 className="mb-1 flex items-center text-lg font-semibold text-primary">
                                {event.status}
                                {isLatest && <Badge className="ml-3 bg-green-100 text-green-700">Current</Badge>}
                              </h3>
                              <time className="block mb-2 text-sm font-normal leading-none text-gray-500">
                                <Clock className="inline-block w-4 h-4 mr-1 align-text-bottom" />
                                {formatDateTime(event.changed_at)}
                              </time>
                              {event.note && (
                                <p className="text-base font-normal text-gray-700">{event.note}</p>
                              )}
                            </li>
                          )
                        })}
                      </ol>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No order history available.</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
